import { ItemOption, itemOptionsInit } from 'components/FormInputSelect'
import { useCallback, useEffect, useState } from 'react'
import addressService from 'services/address.service'
import { AddressType, addressInit } from 'types/address.type'

export type AddressProps = {
  states: ItemOption[]
  cities: ItemOption[]
  neighborhoods: ItemOption[]
}

export const addressPropsInit: AddressProps = {
  states: [],
  cities: [],
  neighborhoods: [],
}

export const useAddress = (stateId?: string | null, cityId?: string | null) => {
  const [address, setAddress] = useState<AddressProps>(addressPropsInit)

  const fillStateOptions = useCallback(async () => {
    try {
      const res = await addressService.getAllStateAcronyms()
      setAddress((prevAddress) => ({ ...prevAddress, states: res.data }))
    } catch (error) {
      console.error('Error in fillStateOptions:', error)
    }
  }, [setAddress])

  const fillCityOptions = useCallback(
    async (stateId: string) => {
      try {
        if (!stateId || stateId === '0') return
        const res = await addressService.getAllCitiesByStateId(stateId)
        setAddress((prevAddress) => ({ ...prevAddress, cities: res.data }))
      } catch (error) {
        console.error('Error in fillCityOptions:', error)
      }
    },
    [setAddress],
  )

  const fillNeighborhoodOptions = useCallback(
    async (cityId: string) => {
      try {
        if (!cityId || cityId === '0') return
        const res = await addressService.getAllNeighbourhoodByCityId(cityId)
        setAddress((prevAddress) => ({ ...prevAddress, neighborhoods: res.data }))
      } catch (error) {
        console.error('Error in fillNeighborhoodOptions:', error)
      }
    },
    [setAddress],
  )

  const setInitCityValues = useCallback(() => {
    setAddress({
      ...address,
      cities: [itemOptionsInit],
      neighborhoods: [itemOptionsInit],
    })
  }, [address, setAddress])

  const setInitNeighbourhoodValues = useCallback(() => {
    setAddress({ ...address, neighborhoods: [itemOptionsInit] })
  }, [address, setAddress])

  const getAddressByZipCode = async (code: string): Promise<AddressType> => {
    try {
      const formattedZipCode = code.replace(/\D/g, '')
      const res = await addressService.getByZipCode(formattedZipCode)
      if (res) return res.data
      return addressInit
    } catch (error) {
      console.error('Error in fillAll:', error)
      return addressInit
    }
  }

  useEffect(() => {
    fillStateOptions()
  }, [fillStateOptions])

  useEffect(() => {
    if (stateId && stateId !== '0') {
      fillCityOptions(stateId)
    } else {
      setInitCityValues()
    }
  }, [stateId])

  useEffect(() => {
    if (cityId && cityId !== '0') {
      fillNeighborhoodOptions(cityId)
    } else {
      setInitNeighbourhoodValues()
    }
  }, [cityId])

  return {
    address: {
      stateOptions: address.states,
      cityOptions: address.cities,
      neighborhoodOptions: address.neighborhoods,
    },
    getAddressByZipCode,
  }
}
